<template>
  <div class="partner-widget-wrapper">
    <iframe
      ref="iframeRef"
      class="partner-widget-iframe"
      title="Партнёрский виджет"
      scrolling="no"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
    />
  </div>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import { buildUrl } from '@/shared/utils/url';
  import type { PartnerWidgetData } from '@/shared/types/partnerWidget';

  interface IProps {
    experienceData?: PartnerWidgetData;
    price?: 'off' | 'on';
    ids?: string;
    experiences?: string | number[];
    city?: string;
    region?: string;
    country?: string;
    type?: string;
    generalTag?: string;
    template?: 'horizontal' | 'horizontal-list';
    features?: string[];
    num?: number;
    preContent?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    features: () => ['notitle', 'logo', 'nolistbutton', 'emptyDummy'],
    preContent: false,
  });

  const isV2 = !!props.experienceData;

  const features = isV2
    ? (props.experienceData!.widget_params.features ?? ['notitle', 'logo', 'nolistbutton', 'emptyDummy'])
    : [...props.features, ...(props.price === 'on' ? ['price'] : [])];

  const preContent = isV2 ? (props.experienceData!.widget_params.preContent ?? false) : props.preContent;
  const featuresStr = features.join(',');

  const iframeRef = ref<HTMLIFrameElement>();

  const onMessage = (event: MessageEvent) => {
    const height = event.data?.height ?? event.data?.newHeight;
    if (height && iframeRef.value) {
      iframeRef.value.style.height = `${height}px`;
    }
  };

  const initV2 = (iframe: HTMLIFrameElement) => {
    const data = props.experienceData!;
    iframe.src = buildUrl('https://experience.tripster.ru/partner/v2/', {
      experienceData: JSON.stringify({
        ...data,
        widget_params: { ...data.widget_params, features: featuresStr, preContent },
      }),
      replace_missing_ids: false,
      features: featuresStr,
      preContent,
      partner: 'tripster_journal',
    });
    window.addEventListener('message', onMessage);
  };

  const initLegacy = (iframe: HTMLIFrameElement) => {
    const id = Math.floor(Math.random() * 1000000);
    const scriptId = `tripster-widget-${id}`;
    const experiencesParam =
      props.ids ?? (Array.isArray(props.experiences) ? props.experiences.join(',') : props.experiences);

    const scriptUrl = `${buildUrl('https://experience.tripster.ru/partner/widget.js', {
      experiences: experiencesParam,
      city: props.city,
      region: props.region,
      country: props.country,
      type: props.type,
      tag: props.generalTag,
      view: 'experience',
      template: props.template ?? 'horizontal-list',
      mobile: 'list',
      order: 'top',
      width: '100%',
      num: props.num ?? (props.ids?.split(',').length || 3),
      version: 2,
      partner: 'tripster_journal',
      features: featuresStr,
      script_id: scriptId,
      replace_missing_ids: false,
    })}&replace_missing_ids=false`;

    iframe.onload = () => {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }

      const container = doc.createElement('div');
      container.id = `tripster-widget-container-${id}`;
      const scriptEl = doc.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.src = scriptUrl;
      scriptEl.async = true;
      doc.body.appendChild(container);
      container.appendChild(scriptEl);

      const sync = () => {
        iframe.style.height = `${doc.documentElement.scrollHeight || doc.body.scrollHeight}px`;
      };
      new ResizeObserver(sync).observe(doc.body);
      sync();
      scriptEl.onload = () => requestAnimationFrame(sync);
      setTimeout(sync, 1500);
    };
    iframe.srcdoc = '<!DOCTYPE html><html><body></body></html>';
  };

  onMounted(() => {
    const iframe = iframeRef.value;
    if (iframe) {
      (isV2 ? initV2 : initLegacy)(iframe);
    }
  });

  onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<style lang="scss" scoped>
  .partner-widget-wrapper {
    position: relative;
    box-sizing: border-box;
    margin: 24px -8px -8px -12px;
  }

  .partner-widget-iframe {
    display: block;
    overflow: hidden;
    width: 100%;
    min-height: 200px;
    border: none;
  }
</style>
